import moment, { Moment } from 'moment';

export function pad(n: number | string, width: number, z: string) {
  z = z || '0';
  n = n + '';
  return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

export const hoursValidatorPattern = () => '^([0-1]?[0-9]|2[0-3])$';
export const minutesValidatorPattern = () => '^([0-5]?[0-9])$';
export const secondsValidatorPattern = () => '^([0-5]?[0-9])$';

export function parseToLocaleDateString(
  date: Date | Moment,
  locale: Intl.LocalesArgument,
  options?: Intl.DateTimeFormatOptions
) {
  date = momentToDate(date);
  return date.toLocaleDateString(locale, options);
}

export function momentToDate(date: Date | Moment) {
  if (moment.isMoment(date)) {
    date = date.toDate();
  }
  return date;
}

export function combineDateTime(date: Date | Moment | string, time: string): Date {
  if (typeof date === 'string') {
    date = new Date(date);
  } else {
    date = momentToDate(date);
  }

  const [hours, minutes, seconds] = time.split(':');

  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hours ? Number(hours) : 0,
    minutes ? Number(minutes) : 0,
    seconds ? Number(seconds) : 0
  );
}

export function combineDateTimeLocale(date: Date | string, time: string): Date {
  const baseDate = new Date(date);
  const [hours, minutes, seconds] = time.split(':').map(Number);

  // Costruisci la data in orario locale
  const localDate = new Date(
    baseDate.getFullYear(),
    baseDate.getMonth(),
    baseDate.getDate(),
    hours || 0,
    minutes || 0,
    seconds || 0,
    0
  );

  // Calcolo offset fuso in minuti e lo converto in millisecondi
  const timezoneOffset = localDate.getTimezoneOffset() * 60000;

  const utcDate = new Date(localDate.getTime() - timezoneOffset);

  return utcDate;
}

export function combineDateTimeLocaleFromISO(dateTime: Date | string): Date {
  const inputDate = new Date(dateTime);
  
  // Estrai ore, minuti e secondi dalla data di input
  const hours = inputDate.getHours();
  const minutes = inputDate.getMinutes();
  const seconds = inputDate.getSeconds();

  // Costruisci la data in orario locale
  const localDate = new Date(
    inputDate.getFullYear(),
    inputDate.getMonth(),
    inputDate.getDate(),
    hours,
    minutes,
    seconds,
    0
  );

  // Calcolo offset fuso in minuti e lo converto in millisecondi
  const timezoneOffset = localDate.getTimezoneOffset() * 60000;

  const utcDate = new Date(localDate.getTime() - timezoneOffset);

  return utcDate;
}


export const DATE_LOCALE_GB_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

export function timeSince(date: Date): { value: number; label: string } {
  const currentDate = new Date();
  const seconds = Math.floor((currentDate.getTime() - date.getTime()) / 1000);

  let interval = seconds / 31536000;
  if (interval > 1) {
    return { value: Math.floor(interval), label: 'years ago' };
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return { value: Math.floor(interval), label: 'months ago' };
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return { value: Math.floor(interval), label: 'days ago' };
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return { value: Math.floor(interval), label: 'hours ago' };
  }
  interval = seconds / 60;
  if (interval > 1) {
    return { value: Math.floor(interval), label: 'minutes ago' };
  }
  return { value: Math.floor(seconds), label: 'seconds ago' };
}

export function formatDate(
  date: Date,
  locale: string,
  showYear: boolean
): string {
  return (
    date?.toLocaleDateString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: showYear ? 'numeric' : undefined,
    }) ?? ''
  );
}

export function formatTime(
  date: Date | string,
  locale: string,
  hour12: boolean,
  showSeconds: boolean = false
): string {
  if (typeof date === 'string') {
    date = new Date(date);
  }

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  return date.toLocaleTimeString(locale, {
    second: showSeconds ? '2-digit' : undefined,
    hour: '2-digit',
    minute: '2-digit',
    hour12,
  });
}

export function getDateRangeTooltip(
  fromDate: Date,
  toDate: Date,
  showTime: boolean = false,
  lang: string,
  showYear: boolean = true,
  hour12: boolean = true,
  useLocale?: string
): string {
  let locale = lang;
  const fromDateText = formatDate(fromDate, useLocale ?? locale, showYear);
  const fromTimeText =
    fromDate && showTime
      ? formatTime(fromDate, useLocale ?? locale, hour12)
      : '';
  const toDateText = formatDate(toDate, useLocale ?? locale, showYear);
  const toTimeText =
    toDate && showTime ? formatTime(toDate, useLocale ?? locale, hour12) : '';

  const from = showTime ? `${fromTimeText} ${fromDateText}` : fromDateText;
  const to = showTime ? `${toTimeText} ${toDateText}` : toDateText;

  return from + ' – ' + to;
}

export const normalizeDate = (date: number | string | Date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

export const normalizeDateSec = (date: number | string | Date) => {
  const d = new Date(date);
  d.setSeconds(0, 0);
  return d.getTime();
};

export const normalizeDateOnly = (date: string | Date): number => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.getDate();
}

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month, 0).getDate();
};

export const getDaysInRange = (start: Date, end: Date) => {
  let totalDays = 0;
  start.setHours(12, 0, 0, 0);
  end.setHours(12, 0, 0, 0);
  // If the dates are in the same month and year
  if (
    start.getFullYear() === end.getFullYear() &&
    start.getMonth() === end.getMonth()
  ) {
    totalDays =
      Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  } else {
    // Add days from the start date to the end of the start month
    totalDays +=
      getDaysInMonth(start.getFullYear(), start.getMonth() + 1) -
      start.getDate() +
      1;
    // Add days for the full months in between
    let currentMonth = start.getMonth() + 2;
    let currentYear = start.getFullYear();
    while (
      currentYear < end.getFullYear() ||
      (currentYear === end.getFullYear() && currentMonth < end.getMonth() + 1)
    ) {
      totalDays += getDaysInMonth(currentYear, currentMonth);
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    // Add days from the beginning of the end month to the end date
    totalDays += end.getDate();
  }
  return totalDays;
};

export const getFormattedXValuesDate = (
  xValues: string[],
  resolutionId: string,
  locale: string,
  fromTs: Date | null,
  toTs: Date | null
): string[] => {
  const dailyList: string[] = [];
  const weeklyList = new Set();
  const monthlyList = new Set();
  const quarterBasedList = new Set();
  const yearBasedList = new Set();

  xValues.forEach((x) => {
    const dateFormat = new Date(Date.parse(x));
    dailyList.push(
      dateFormat.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    );
    monthlyList.add(
      dateFormat.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    );
    yearBasedList.add(
      dateFormat.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    );
  });

  switch (resolutionId) {
    case '1':
      if (fromTs && toTs) {
        const fromDate = new Date(fromTs);
        const toDate = new Date(toTs);
        const fromDateFormatted = fromDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        const toDateFormatted = toDate.toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        });
        return [`${fromDateFormatted} - ${toDateFormatted}`];
      }
      return ['Overall'];
    case '2':
      return [...dailyList];
    case '7':
      const hourlyList: string[] = [];
      xValues.forEach((x) => {
        const dateFormat = new Date(Date.parse(x));
        const timeFormatted = dateFormat.toLocaleTimeString(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
        hourlyList.push(timeFormatted);
      });
      return hourlyList;
    case '3':
      xValues.forEach((v, index) => weeklyList.add(`W${index + 1}`));
      return [...(Array.from(weeklyList) as string[])];
    case '4':
      return [...(Array.from(monthlyList) as string[])];
    case '5':
      xValues.forEach((v, index) => quarterBasedList.add(`Q${index + 1}`));
      return [...(Array.from(quarterBasedList) as string[])];
    case '6':
      return [...(Array.from(yearBasedList) as string[])];
    default:
      return [];
  }
};
